import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import { Identifiable } from '@custis/common';
import { TTableColumn } from '../../../models';
import { getCellField, getFieldProps } from '../fields';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import s from './EditableRow.module.less';
import { KEYBOARD_KEYS, NEW_ROW_ID } from '../../../constants';
import { observer } from 'mobx-react';
import { IdType } from 'react-table';
import classNames from 'classnames';
import { RowValidationPopover } from './RowValidationPopover';
import { getInputRulesFromColumnItem, useSubmitValidate } from './rowHelpers';
import { RowValidationPopoverData } from './models';

type ReactHTML = JSX.IntrinsicElements;

interface Props<T extends Identifiable, TFilter, TSorting> {
  columns: TTableColumn<T, TFilter, TSorting>[];
  visible: boolean;
  data?: Partial<T>;
  width: number;
  hiddenColumns: IdType<T>[];

  onCreate?(row: T): Promise<void>;

  onFormChange?(row: Partial<T>): void;

  onHide(): void;
}

export const NewRow = observer(
  <T extends Identifiable, TFilter, TSorting>({
    columns,
    onCreate,
    visible,
    onHide,
    data,
    onFormChange,
    width,
    hiddenColumns,
  }: Props<T, TFilter, TSorting>) => {
    const [form] = Form.useForm<T>();
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
    const ref = useRef<HTMLDivElement>(null);
    const [popoverData, setPopoverData] = useState<RowValidationPopoverData>(null);
    const [clickableElements, setClickableElements] = useState<Element[]>([]);
    const errorRef = useRef<HTMLDivElement>(null);
    const visibleColumns = useMemo(
      () => columns.filter((item) => item.id && !hiddenColumns.includes(item.id)),
      [columns, hiddenColumns],
    );

    const handleHide = () => {
      onHide();
      setPopoverData(null);
    };

    useEffect(() => {
      columns.forEach((item) => {
        if (item.fieldType && ['select', 'multi-select'].includes(item.fieldType)) {
          item?.store?.reset();
        }
      });
    }, []);

    useEffect(() => {
      const requiredColumns = columns.filter((item) => item.required && item.id).map((item) => item.id);
      const touchedFieldsInitial = requiredColumns.reduce<Record<string, boolean>>((acc, curr: string | undefined) => {
        if (curr) {
          acc[curr] = false;
        }
        return acc;
      }, {});
      setTouchedFields(touchedFieldsInitial);
    }, []);

    const handleCreate = (row: T) => {
      if (onCreate) {
        void onCreate(row)
          .then(() => {
            form.resetFields();
            handleHide();
          })
          .catch(() => {
            form.resetFields();
            handleHide();
          });
      }
    };

    const setAllFieldsTouched = () => {
      setTouchedFields((prev) => {
        Object.keys(prev).forEach((item) => {
          prev[item] = true;
        });
        return prev;
      });
    };

    const { onSubmit } = useSubmitValidate({
      columns,
      visible,
      setPopoverData,
      form,
      handleSave: handleCreate,
      setAllFieldsTouched,
    });

    useEffect(() => {
      if (data) {
        const fields = Object.entries(data)
          .filter(([_, value]) => !!value)
          .map(([key, value]) => ({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            name: key as any,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value: value,
          }));
        form.setFields(fields);
      }
    }, []);

    useEffect(() => {
      if (visible) {
        const refElement = ref.current;
        if (refElement && !clickableElements.includes(refElement)) {
          setClickableElements((prevState) => [...prevState, refElement]);
        }
      }
    }, [columns, ref.current, visible]);

    useEffect(() => {
      if (popoverData) {
        const errorElement = errorRef.current;
        if (errorElement && !clickableElements.includes(errorElement)) {
          setClickableElements((prevState) => [...prevState, errorElement]);
        }
      }
    }, [popoverData]);

    const onPressEnter = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === KEYBOARD_KEYS.ENTER && visible) {
          onSubmit();
        }
      },
      [visible],
    );

    useEffect(() => {
      window.addEventListener('keydown', onPressEnter);

      return () => {
        window.removeEventListener('keydown', onPressEnter);
      };
    }, []);

    const onClickRow = useCallback(() => {
      columns.forEach((item) => {
        if (item.fieldType && ['select', 'multi-select'].includes(item.fieldType)) {
          const element = document.querySelector(`.${item.id as string}-select`);
          if (element) {
            setClickableElements((prevState) => [...prevState, element]);
          }
        }
        if (item.fieldType === 'date') {
          const element = document.querySelector(`.${item.id as string}-date`);
          if (element) {
            setClickableElements((prevState) => [...prevState, element]);
          }
        }
      });
    }, [columns]);

    const handleFormChange = useCallback((row: Partial<T>) => {
      if (onFormChange) {
        onFormChange(row);
        Object.entries(row).forEach(([key, value]) => {
          if (value) {
            setTouchedFields((prev) => ({
              ...prev,
              [key]: true,
            }));
          }
        });
      }
    }, []);

    useOutsideClick(clickableElements, onSubmit);

    return (
      <div className={s.Wrapper} ref={ref} id={NEW_ROW_ID} style={{ width }}>
        <div className={s.Row} onClick={onClickRow}>
          <Form<T> form={form} className={s.Form} onValuesChange={handleFormChange}>
            {visibleColumns.map((item) => {
              if (item.fieldType === 'no-field') {
                return (
                  <React.Fragment key={item.id}>
                    <div style={{ width: item.width, flex: `${item.width ?? 150} 0 auto` }} />
                  </React.Fragment>
                );
              } else {
                const rules = getInputRulesFromColumnItem<T, TFilter, TSorting>(item);
                return (
                  <React.Fragment key={item.id}>
                    {
                      <Form.Item
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                        name={item.id as any}
                        style={{
                          width: item.width,
                          flex: `${item.width ?? 150} 0 auto`,
                          ...(item.formItemProps?.style || {}),
                        }}
                        className={classNames(
                          s.Cell,
                          item.formItemProps?.className,
                          item.id && !touchedFields[item.id] && s.NotError,
                        )}
                        valuePropName={item.fieldType === 'checkbox' ? 'checked' : 'value'}
                        {...{ ...item.formItemProps, rules }}
                      >
                        {item.fieldType &&
                          React.createElement(
                            getCellField(item) as unknown as keyof ReactHTML,
                            getFieldProps<T, TFilter, TSorting>(item, {
                              isCreate: true,
                            }),
                          )}
                      </Form.Item>
                    }
                  </React.Fragment>
                );
              }
            })}
          </Form>
        </div>
        <RowValidationPopover
          {...{ popoverData, errorRef }}
          onCancel={handleHide}
          onContinue={() => {
            setPopoverData(null);
          }}
        />
      </div>
    );
  },
);
